import { KeyboardArrowRight } from '@mui/icons-material'
import { Chip, Stack, Typography } from '@mui/material'
import { Roles } from '@omnea-digital/toolbox'
import Image from 'next/image'
import { useTranslation } from 'react-i18next'
import { getRoleLabelFromUserConfig } from '@omnea-app/fe-auth/roles'
import { HighlightedText } from '@omnea-app/fe-legacy/components/shared/ui/HighlightedText/HighlightedText'
import { IndexRange } from '@omnea-app/fe-legacy/utils/string/fuzzySearchList'
import { useFeatureFlags } from '@omnea-app/fe-org-settings/FeatureFlagsProvider'
import { InitialsAvatar } from '@omnea-app/fe-users-and-groups/avatar/InitialsAvatar'
import { AvailableOrganisation } from '@omnea-app/login/loginFrontendClient'
import { FeatureFlagsEnum } from '@omnea-app/packages/universal/feature-flags/featureFlags'
import { Nullish } from '@omnea-app/packages/universal/utility-types/utilityTypes'
import { OrganisationMenuItemButtonWrapper } from './OrganisationMenuItemButtonWrapper'

interface OrganisationMenuItemProps {
  organisation: AvailableOrganisation
  highlightedRanges: Nullish<IndexRange[]>
  handlePickOrganisation: (organisationId: string) => void
  isFirst: boolean
  isQaOrg: boolean
}

export const OrganisationMenuItem: React.FC<OrganisationMenuItemProps> = ({
  organisation,
  highlightedRanges,
  handlePickOrganisation,
  isFirst,
  isQaOrg,
}) => {
  const { t } = useTranslation('settings')
  // P2P_ADMIN_MODE: when the active org has the flag on, label Admins as "P2P User" across every
  // row in the switcher (intentionally including other orgs — we only read the active org's flag).
  const p2pAdminMode = useFeatureFlags()[FeatureFlagsEnum.P2P_ADMIN_MODE]

  const isFirstSearchResult = isFirst && !!highlightedRanges

  return (
    <OrganisationMenuItemButtonWrapper
      onClick={() => handlePickOrganisation(organisation.id)}
      data-is-first-search-result={isFirstSearchResult}
    >
      <Stack direction="row" gap={1} alignItems="center">
        {organisation.iconUrl ? (
          <picture>
            <img src={organisation.iconUrl} alt={organisation.name} height={20} width={20} />
          </picture>
        ) : (
          <InitialsAvatar
            fullName={organisation.name[0] || '!'}
            size={20}
            sx={{ backgroundColor: 'bg.neutral.muted', color: 'fg.subtle' }}
            variant="square"
          />
        )}
        <Typography variant="body-medium" flexGrow={1} textAlign="left" margin={0} noWrap>
          <HighlightedText text={organisation.name} highlightedRanges={highlightedRanges} />
        </Typography>
        {isQaOrg && <Image src="/app/assets/qa-chip.svg" alt="qa chip" height={18} width={26} />}
      </Stack>
      <Stack direction="row" alignItems="center" gap={2}>
        <Chip
          label={getRoleLabelFromUserConfig({
            t,
            userConfig: {
              role: organisation.user.role as Roles,
              hasConfigPermissions: organisation.user.hasConfigPermissions,
              isViewOnly: organisation.user.isViewOnly,
            },
            p2pAdminMode,
          })}
          color={organisation.user.role === 'supplier' ? 'neutral' : 'info'}
          size="tiny"
        />
        <KeyboardArrowRight
          sx={{
            width: 18,
            height: 18,
            color: 'fg.muted',
          }}
        />
      </Stack>
    </OrganisationMenuItemButtonWrapper>
  )
}
